import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useMe } from "@/hooks/use-auth";
import { useCreateUom, useUpdateUom } from "@/hooks/use-uoms";
import { getErrorMessage } from "@/lib/error-message";

import { defaultUomValues, uomSchema, type UomFormValues } from "./uom-schema";

import type { UnitOfMeasurement } from "@artisancode/api-types";

export function useUomForm(
  open: boolean,
  editing: UnitOfMeasurement | null,
  onSuccess: () => void,
) {
  const { mutate: create, isPending: isCreating } = useCreateUom();
  const { mutate: update, isPending: isUpdating } = useUpdateUom();
  const { data: me } = useMe();

  const form = useForm<UomFormValues>({
    resolver: zodResolver(uomSchema),
    defaultValues: defaultUomValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(
        editing
          ? {
              name: editing.name,
              symbol: editing.symbol,
              category: editing.category,
            }
          : { ...defaultUomValues, workspaceId: me?.workspaceId ?? "" },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, me?.workspaceId]);

  function onSubmit(values: UomFormValues) {
    if (editing) {
      update(
        {
          id: editing.id,
          name: values.name,
          symbol: values.symbol,
          category: values.category,
        },
        {
          onSuccess: () => {
            toast.success("Satuan berhasil diperbarui.");
            onSuccess();
          },
          onError: (err: Error) => {
            if (
              err.message?.toLowerCase().includes("simbol") ||
              err.message?.toLowerCase().includes("symbol")
            ) {
              form.setError("symbol", { message: err.message });
            } else if (
              err.message?.toLowerCase().includes("kategori") ||
              err.message?.toLowerCase().includes("category")
            ) {
              form.setError("category", { message: err.message });
            } else {
              toast.error(getErrorMessage(err, "Gagal memperbarui satuan."));
            }
          },
        },
      );
    } else {
      create(
        { ...values, workspaceId: values.workspaceId || undefined },
        {
          onSuccess: () => {
            toast.success("Satuan berhasil ditambahkan.");
            onSuccess();
          },
          onError: (err: Error) => {
            if (
              err.message?.toLowerCase().includes("simbol") ||
              err.message?.toLowerCase().includes("symbol")
            ) {
              form.setError("symbol", { message: err.message });
            } else {
              toast.error(getErrorMessage(err, "Gagal menambahkan satuan."));
            }
          },
        },
      );
    }
  }

  return {
    form,
    isSubmitting: isCreating || isUpdating,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
